import { defineComponent } from 'vue';
import {
    QBtn, QBanner, QSpinner, QSelect, QInput, QIcon, Notify,
} from 'quasar';
import { AgGridVue } from 'ag-grid-vue3';
import utils from '../../../../../Shared/utils.jsx';
import { SENTENCE_CASE_BUTTON_CLASS } from '../../../../../Shared/buttonAppearance.js';
import formatApiError from './formatApiError.js';
import GlobalVarObjectEditor from './GlobalVarObjectEditor.jsx';
import {
    GLOBAL_VAR_API,
    GLOBAL_COLLECTION_ID,
    TYPE_VALUES,
    recordKey,
    getDisplayValue,
    setDisplayValue,
    applyTypeChange,
    planSaveChanges,
    createEmptyRow,
    rowIdFor,
    typeLabel,
    isBuiltinCollection,
    isValidVariableName,
    variableNameError,
    projectCollectionId,
    rowsMatchingCollection,
    selectGlobalRows,
} from './globalVarValue.js';
import { editCellAfterGridFlush, suppressTabToColumn } from './gridEditNavigation.js';
import '../../../css/BasicButton.css';
import '../styles/flowSurfaceSwitch.css';

const BTN = `${SENTENCE_CASE_BUTTON_CLASS} q-my-xs`;

function encodeQuery(params) {
    return Object.entries(params)
        .filter(([, v]) => v !== undefined && v !== null)
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
        .join('&');
}

export default defineComponent({
    name: 'flow-v2-global-vars-panel',
    components: {
        QBtn, QBanner, QSpinner, QSelect, QInput, QIcon, AgGridVue,
    },
    props: {
        projectId: { type: String, default: '' },
    },
    data() {
        return {
            loading: true,
            loadingRows: false,
            saving: false,
            error: null,
            scope: 'global',
            namedCollection: '',
            newCollectionDraft: '',
            namedCollectionOptions: [],
            rows: [],
            originalByKey: new Map(),
            quickFilter: '',
            selectedRows: [],
            gridApi: null,
            dirty: false,
        };
    },
    computed: {
        collection() {
            if (this.scope === 'project')
                return projectCollectionId(this.projectId);
            if (this.scope === 'named')
                return this.namedCollection || '';
            return GLOBAL_COLLECTION_ID;
        },
        namedReady() {
            if (this.scope === 'project')
                return !!projectCollectionId(this.projectId);
            if (this.scope === 'named')
                return !!String(this.namedCollection || '').trim();
            return true;
        },
        scopeHint() {
            if (this.scope === 'project')
                return 'Variables for this project only. They are stored under this project’s id, separate from Global and from custom names.';
            if (this.scope === 'named')
                return 'Custom collections are independent of Global, this project, and each other. Open a saved collection, or type a new name and press Enter. Variables can be added only after a collection is selected.';
            return 'Global variables are available to every flowchart. They are stored apart from this project and from every custom collection.';
        },
        openCollectionValue() {
            const name = this.namedCollection || '';
            if (!name)
                return null;
            return (this.namedCollectionOptions || []).some((o) => o.value === name) ? name : null;
        },
        isPendingCollection() {
            const name = String(this.namedCollection || '').trim();
            if (!name || this.scope !== 'named')
                return false;
            return !(this.namedCollectionOptions || []).some((o) => o.value === name);
        },
        isDeleteDisabled() {
            return this.selectedRows.length < 1;
        },
        columnDefs() {
            return [
                {
                    field: 'Name',
                    headerName: 'Name',
                    headerTooltip: 'Letter or underscore, then letters, digits, or underscores',
                    editable: true,
                    flex: 1,
                    minWidth: 140,
                    sortable: true,
                    filter: true,
                    valueSetter: (p) => {
                        const next = String(p.newValue ?? '').trim();
                        const message = variableNameError(next);
                        if (message) {
                            this.error = message;
                            return false;
                        }
                        const dup = (this.rows || []).some((r) => r !== p.data && String(r.Name || '').trim() === next);
                        if (dup) {
                            this.error = `Duplicate name “${next}” in this collection.`;
                            return false;
                        }
                        this.error = null;
                        p.data.Name = next;
                        return true;
                    },
                    suppressKeyboardEvent: (params) => suppressTabToColumn(params, 'Type', 'Enter'),
                    cellStyle: (p) => {
                        const name = p.value == null ? '' : String(p.value);
                        if (name && !isValidVariableName(name))
                            return { background: '#ffebee', color: '#b71c1c' };
                        return null;
                    },
                },
                {
                    field: 'Type',
                    headerName: 'Type',
                    editable: true,
                    width: 160,
                    sortable: true,
                    cellEditor: 'agSelectCellEditor',
                    cellEditorParams: {
                        values: TYPE_VALUES,
                    },
                    valueFormatter: (p) => typeLabel(p.value),
                    suppressKeyboardEvent: (params) => suppressTabToColumn(params, 'Value'),
                },
                {
                    colId: 'Value',
                    headerName: 'Value',
                    editable: true,
                    flex: 2,
                    minWidth: 180,
                    sortable: false,
                    valueGetter: (p) => getDisplayValue(p.data),
                    valueSetter: (p) => {
                        const result = setDisplayValue(p.data, p.newValue);
                        if (!result.ok) {
                            this.error = result.error || 'Invalid value';
                            return false;
                        }
                        this.error = null;
                        return true;
                    },
                    suppressKeyboardEvent: (params) => {
                        if (!params.editing)
                            return false;
                        if ((params.data?.Type || 'string') !== 'object')
                            return false;
                        // Enter stays in the Ace editor (newline). Ctrl/Cmd+Enter commits.
                        return params.event?.key === 'Enter' && !params.event.ctrlKey && !params.event.metaKey;
                    },
                    cellEditorSelector: (params) => {
                        const type = params.data?.Type || 'string';
                        if (type === 'bool') {
                            return {
                                component: 'agCheckboxCellEditor',
                            };
                        }
                        if (type === 'object') {
                            return {
                                component: 'GlobalVarObjectEditor',
                                popup: true,
                            };
                        }
                        if (type === 'int' || type === 'float') {
                            return { component: 'agNumberCellEditor' };
                        }
                        return { component: 'agTextCellEditor' };
                    },
                    cellDataType: false,
                    cellRenderer: (params) => {
                        const type = params.data?.Type || 'string';
                        if (type === 'bool')
                            return params.value ? 'true' : 'false';
                        if (type === 'object') {
                            const s = params.value == null ? '' : String(params.value);
                            return s.length > 80 ? `${s.slice(0, 80)}…` : s;
                        }
                        return params.value == null ? '' : String(params.value);
                    },
                },
            ];
        },
        defaultColDef() {
            return {
                resizable: true,
                suppressMovable: true,
            };
        },
    },
    mounted() {
        this.bootstrap();
    },
    methods: {
        recomputeDirty() {
            const plan = planSaveChanges(this.originalByKey, this.rows, this.collection);
            this.dirty = plan.toDelete.length > 0 || plan.toSave.length > 0;
        },
        async bootstrap() {
            this.loading = true;
            this.error = null;
            try {
                await this.loadCollections();
                await this.loadRows();
            }
            catch (err) {
                this.error = formatApiError(err);
            }
            finally {
                this.loading = false;
            }
        },
        async loadCollections() {
            const url = `${GLOBAL_VAR_API}/CountByCollection?${encodeQuery({ MaxRows: 500, UseCache: false })}`;
            const raw = await utils.api.get(url, true, false);
            const list = Array.isArray(raw) ? raw : [];
            const names = new Set();
            for (const item of list) {
                const name = item?.Collection != null ? String(item.Collection).trim() : '';
                if (!name || isBuiltinCollection(name, this.projectId))
                    continue;
                names.add(name);
            }
            const sorted = [...names].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
            this.namedCollectionOptions = sorted.map((c) => ({ value: c, label: c }));
            if (this.namedCollection && !names.has(this.namedCollection) && !this.dirty)
                this.namedCollection = '';
        },
        async fetchAllRows() {
            const url = `${GLOBAL_VAR_API}/ListByCollectionName?${encodeQuery({ MaxRows: 2000, UseCache: false })}`;
            const raw = await utils.api.get(url, true, false);
            return Array.isArray(raw) ? raw : [];
        },
        async fetchCollectionRows(collection) {
            const url = `${GLOBAL_VAR_API}/ListByCollection?${encodeQuery({
                Collection: collection ?? '',
                MaxRows: 2000,
                UseCache: false,
            })}`;
            const raw = await utils.api.get(url, true, false);
            return rowsMatchingCollection(Array.isArray(raw) ? raw : [], collection ?? '');
        },
        snapshotRows(list, collection) {
            for (const r of list) {
                const name = r?.Name == null ? '' : String(r.Name);
                if (!name)
                    continue;
                this.originalByKey.set(recordKey(collection, name), {
                    Collection: collection ?? '',
                    Name: name,
                    Type: r.Type || 'string',
                    ValueString: r.ValueString,
                    ValueInteger: r.ValueInteger,
                    ValueFloat: r.ValueFloat,
                    ValueBoolean: r.ValueBoolean,
                    ValueObject: r.ValueObject,
                });
            }
        },
        async loadRows() {
            if (this.scope === 'named' && !String(this.namedCollection || '').trim()) {
                this.rows = [];
                this.originalByKey = new Map();
                this.dirty = false;
                this.selectedRows = [];
                return;
            }
            if (this.scope === 'project' && !projectCollectionId(this.projectId)) {
                this.rows = [];
                this.originalByKey = new Map();
                this.dirty = false;
                this.selectedRows = [];
                return;
            }
            this.loadingRows = true;
            this.error = null;
            try {
                let list;
                this.originalByKey = new Map();
                if (this.scope === 'global') {
                    const all = await this.fetchAllRows();
                    const selected = selectGlobalRows(all);
                    this.snapshotRows(selected.primary, GLOBAL_COLLECTION_ID);
                    this.snapshotRows(selected.legacy, '');
                    list = selected.rows;
                }
                else {
                    list = await this.fetchCollectionRows(this.collection);
                    this.snapshotRows(list, this.collection);
                }
                const rows = list.map((r, i) => ({
                    ...r,
                    Collection: this.collection ?? '',
                    Type: r.Type || 'string',
                    _rowId: `srv-${i}-${recordKey(this.collection, r.Name)}`,
                }));
                this.rows = rows;
                this.dirty = false;
                this.selectedRows = [];
                this.$nextTick(() => {
                    this.gridApi?.setGridOption?.('rowData', this.rows);
                });
            }
            finally {
                this.loadingRows = false;
            }
        },
        async confirmDiscard() {
            if (!this.dirty)
                return true;
            return window.confirm('Discard unsaved changes?');
        },
        async setScope(next) {
            if (next === this.scope)
                return;
            if (!(await this.confirmDiscard()))
                return;
            this.scope = next;
            this.dirty = false;
            try {
                await this.loadRows();
            }
            catch (err) {
                this.error = formatApiError(err);
            }
        },
        async createNamedCollection() {
            const next = String(this.newCollectionDraft || '').trim();
            if (!next)
                return;
            if (isBuiltinCollection(next, this.projectId)) {
                this.error = next === projectCollectionId(this.projectId)
                    ? 'That name is this project. Use the Project category for those variables.'
                    : 'That name is reserved for Global. Use the Global category instead.';
                return;
            }
            this.error = null;
            const created = await this.onNamedCollectionUpdate(next);
            if (created !== false)
                this.newCollectionDraft = '';
        },
        async onNamedCollectionUpdate(val) {
            const next = val == null ? '' : String(val).trim();
            if (!next || next === this.namedCollection)
                return false;
            if (isBuiltinCollection(next, this.projectId)) {
                this.error = 'That name is reserved. Use Global or Project for those variables.';
                return false;
            }
            if (!(await this.confirmDiscard()))
                return false;
            this.namedCollection = next;
            try {
                await this.loadRows();
            }
            catch (err) {
                this.error = formatApiError(err);
                return false;
            }
            return true;
        },
        async refreshFromServer() {
            if (!(await this.confirmDiscard()))
                return;
            try {
                await this.loadCollections();
                await this.loadRows();
            }
            catch (err) {
                this.error = formatApiError(err);
            }
        },
        onGridReady(params) {
            this.gridApi = params.api;
        },
        getRowId(params) {
            return rowIdFor(params.data, this.collection);
        },
        onSelectionChanged() {
            this.selectedRows = this.gridApi?.getSelectedRows?.() || [];
        },
        onCellValueChanged(e) {
            if (!e?.data)
                return;
            if (e.colDef?.field === 'Type' && e.newValue !== e.oldValue) {
                applyTypeChange(e.data, e.newValue);
                this.gridApi?.refreshCells?.({ rowNodes: [e.node], force: true });
                this.$nextTick(() => {
                    const idx = e.node?.rowIndex;
                    if (idx != null) {
                        this.gridApi?.startEditingCell?.({
                            rowIndex: idx,
                            colKey: 'Value',
                        });
                    }
                });
            }
            this.recomputeDirty();
        },
        addRow() {
            if (!this.namedReady)
                return;
            const newRow = createEmptyRow(this.collection);
            this.rows = [...this.rows, newRow];
            this.dirty = true;
            this.$nextTick(() => {
                editCellAfterGridFlush(this.gridApi, newRow._rowId, 'Name');
            });
        },
        removeSelected() {
            const selected = this.gridApi?.getSelectedRows?.() || [];
            if (!selected.length)
                return;
            const removeIds = new Set(selected.map((r) => rowIdFor(r, this.collection)));
            this.rows = this.rows.filter((r) => !removeIds.has(rowIdFor(r, this.collection)));
            this.selectedRows = [];
            this.recomputeDirty();
            this.$nextTick(() => {
                this.gridApi?.setGridOption?.('rowData', this.rows);
            });
        },
        async save() {
            if (this.saving)
                return;
            // Stop editing so valueSetter commits
            this.gridApi?.stopEditing?.();
            this.saving = true;
            this.error = null;
            try {
                // Deduplicate names in current collection
                const names = new Set();
                for (const r of this.rows) {
                    const n = String(r.Name || '').trim();
                    const message = variableNameError(n);
                    if (message) {
                        this.error = message;
                        return;
                    }
                    if (names.has(n)) {
                        this.error = `Duplicate name “${n}” in this collection.`;
                        return;
                    }
                    names.add(n);
                    r.Name = n;
                }

                const plan = planSaveChanges(this.originalByKey, this.rows, this.collection);
                for (const d of plan.toDelete) {
                    const q = encodeQuery({
                        Collection: d.Collection ?? '',
                        Name: d.Name ?? '',
                    });
                    await utils.api.request('DELETE', `${GLOBAL_VAR_API}?${q}`);
                }
                for (const payload of plan.toSave) {
                    await utils.api.request('POST', `${GLOBAL_VAR_API}?AutoReplace=true`, payload);
                }

                await this.loadCollections();
                await this.loadRows();

                try {
                    Notify.create({
                        type: 'positive',
                        message: 'Shared variables saved',
                        position: 'top',
                        timeout: 2000,
                    });
                }
                catch {
                    /* Notify plugin optional */
                }
            }
            catch (err) {
                this.error = formatApiError(err);
            }
            finally {
                this.saving = false;
            }
        },
    },
    render() {
        if (this.loading) {
            return (
                <div class="q-pa-lg flex flex-center" style={{ height: '100%' }}>
                    <QSpinner color="primary" size="40px" />
                </div>
            );
        }

        return (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', minHeight: 0, gap: '8px' }}>
                <div class="text-caption text-grey-7">
                    {this.scopeHint}
                </div>

                {this.error ? (
                    <QBanner class="bg-red-1 text-negative" dense rounded>
                        {this.error}
                    </QBanner>
                ) : null}

                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <div class="flow-v2-surface-switch">
                        <QBtn
                            label="Global"
                            unelevated
                            no-caps
                            class={`${BTN} ThemeRightConnector`}
                            color={this.scope === 'global' ? 'primary' : 'grey-3'}
                            text-color={this.scope === 'global' ? 'white' : 'grey-8'}
                            onClick={() => this.setScope('global')}
                        />
                        <QBtn
                            label="Project"
                            unelevated
                            no-caps
                            class={BTN}
                            style={{ borderRadius: 0 }}
                            disable={!projectCollectionId(this.projectId)}
                            color={this.scope === 'project' ? 'primary' : 'grey-3'}
                            text-color={this.scope === 'project' ? 'white' : 'grey-8'}
                            onClick={() => this.setScope('project')}
                        />
                        <QBtn
                            label="Named"
                            unelevated
                            no-caps
                            class={`${BTN} ThemeLeftConnector`}
                            color={this.scope === 'named' ? 'primary' : 'grey-3'}
                            text-color={this.scope === 'named' ? 'white' : 'grey-8'}
                            onClick={() => this.setScope('named')}
                        />
                    </div>
                    {this.scope === 'named' ? (
                        <QSelect
                            dense
                            outlined
                            emit-value
                            map-options
                            options-dense
                            behavior="menu"
                            label="Open collection"
                            style={{ minWidth: '200px', maxWidth: '280px', flex: '0 1 240px' }}
                            disable={!this.namedCollectionOptions.length}
                            modelValue={this.openCollectionValue}
                            options={this.namedCollectionOptions}
                            option-value="value"
                            option-label="label"
                            placeholder={this.namedCollectionOptions.length ? undefined : 'No collections yet'}
                            onUpdate:modelValue={(v) => this.onNamedCollectionUpdate(v)}
                        />
                    ) : null}
                    {this.scope === 'named' ? (
                        <QInput
                            dense
                            outlined
                            hide-bottom-space
                            label="New collection"
                            placeholder="Type a name, then Enter"
                            style={{ minWidth: '220px', maxWidth: '280px', flex: '0 1 240px' }}
                            modelValue={this.newCollectionDraft}
                            onUpdate:modelValue={(v) => { this.newCollectionDraft = v || ''; }}
                            onKeyup={(e) => { if (e.key === 'Enter') this.createNamedCollection(); }}
                        />
                    ) : null}
                    {this.scope === 'named' ? (
                        <QBtn
                            unelevated
                            no-caps
                            color="primary"
                            class={BTN}
                            label="Create"
                            disable={!String(this.newCollectionDraft || '').trim() || this.saving}
                            onClick={() => this.createNamedCollection()}
                        />
                    ) : null}
                    {this.scope === 'named' && this.isPendingCollection ? (
                        <div class="text-caption text-grey-8" style={{ flexBasis: '100%' }}>
                            New collection “{this.namedCollection}”. Add variables, then Save. It is listed under Open collection after it is saved.
                        </div>
                    ) : null}
                    <QInput
                        dense
                        outlined
                        clearable
                        hide-bottom-space
                        placeholder="Search"
                        style={{ flex: '1 1 160px', maxWidth: '280px' }}
                        modelValue={this.quickFilter}
                        onUpdate:modelValue={(v) => { this.quickFilter = v || ''; }}
                        disable={!this.namedReady}
                        v-slots={{
                            prepend: () => <QIcon name="mdi-magnify" />,
                        }}
                    />
                    {utils.tt('Add variable', (
                        <QBtn
                            flat
                            round
                            dense
                            icon="mdi-plus"
                            size="sm"
                            disable={this.loadingRows || this.saving || !this.namedReady}
                            onClick={() => this.addRow()}
                        />
                    ))}
                    {utils.tt('Delete selected', (
                        <QBtn
                            flat
                            round
                            dense
                            icon="mdi-trash-can-outline"
                            size="sm"
                            disable={this.isDeleteDisabled || this.saving}
                            onClick={() => this.removeSelected()}
                        />
                    ))}
                    <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
                        {this.loadingRows ? <QSpinner size="20px" color="primary" class="q-mr-xs" /> : null}
                        {utils.tt('Reload from server', (
                            <QBtn
                                flat
                                round
                                dense
                                icon="mdi-refresh"
                                size="sm"
                                disable={this.loadingRows || this.saving}
                                onClick={() => this.refreshFromServer()}
                            />
                        ))}
                    </div>
                </div>

                <div
                    class="ag-theme-alpine"
                    style={{
                        flex: 1,
                        minHeight: '240px',
                        width: '100%',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        background: 'white',
                    }}
                >
                    <AgGridVue
                        style={{ width: '100%', height: '100%' }}
                        class="ag-theme-alpine"
                        components={{ GlobalVarObjectEditor }}
                        columnDefs={this.columnDefs}
                        defaultColDef={this.defaultColDef}
                        rowData={this.rows}
                        getRowId={this.getRowId}
                        quickFilterText={this.quickFilter}
                        headerHeight={32}
                        rowHeight={36}
                        rowSelection={{ mode: 'multiRow', checkboxes: true, headerCheckbox: true }}
                        singleClickEdit={true}
                        stopEditingWhenCellsLoseFocus={true}
                        animateRows={false}
                        overlayNoRowsTemplate={this.scope === 'named' && !this.namedReady
                            ? 'Type a collection name and press Enter'
                            : (this.scope === 'project'
                                ? 'No variables for this project yet'
                                : (this.scope === 'global'
                                    ? 'No global variables yet'
                                    : 'No variables in this collection'))}
                        onGridReady={this.onGridReady}
                        onSelectionChanged={this.onSelectionChanged}
                        onCellValueChanged={this.onCellValueChanged}
                    />
                </div>

                <div class="row items-center justify-end q-gutter-sm" style={{ flexShrink: 0 }}>
                    {this.dirty ? (
                        <span class="text-caption text-grey-7">Unsaved changes</span>
                    ) : null}
                    <QBtn
                        color="primary"
                        unelevated
                        no-caps
                        class={BTN}
                        label="Save"
                        loading={this.saving}
                        disable={!this.dirty || this.loadingRows || !this.namedReady}
                        onClick={() => this.save()}
                    />
                </div>
            </div>
        );
    },
});
